import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  AMAP_CHARGING_DESTINATION,
  type ChargingStation,
  type ChargingStationSource,
  type GeoPoint,
  formatStationDistance,
  getStationDisplayName
} from "../../config/amapChargingDemo";
import {
  formatCarwashDistance,
  getCarwashDisplayName,
  type CarwashShop,
  type CarwashShopSource
} from "../../config/amapCarwashDemo";
import {
  formatFuelDistance,
  formatFuelPrice,
  getFuelStationDisplayName,
  type FuelStation,
  type FuelStationSource
} from "../../config/amapFuelDemo";
import {
  formatPickupDistance,
  getPickupPointDisplayName,
  type ParcelPickupPoint,
  type ParcelPointSource
} from "../../config/amapParcelDemo";
import {
  LONGYAO_WAITING_FALLBACK_POIS,
  type WaitingRoutePoi
} from "../../config/amapWaitingRouteDemo";
import {
  loadAmapScript,
  type RouteGeometry,
  type VehicleLocationState,
  type VehicleLocationStatus
} from "../../services/amapClient";
import type { TraditionalDesktopDestinationTarget } from "./TraditionalDesktopMapDestination";

type MapStatus = "loading" | "ready" | "fallback";
type AmapLayerMode = "charging" | "parcel" | "carwash" | "refuel" | "waiting";
type WaitingRouteDataSource =
  | "amap-live"
  | "amap-cache"
  | "amap-snapshot"
  | "amap-snapshot-cache";

const AMAP_DRAG_EXTRA_SPEED = 1.2;
const AMAP_WHEEL_PAN_SPEED = 2.05;
const AMAP_PINCH_ZOOM_SPEED = 0.0095;
const AMAP_PINCH_MIN_STEP = 0.14;
const AMAP_PINCH_MAX_STEP = 0.72;
const MOUSE_WHEEL_ZOOM_DELTA_THRESHOLD = 32;
const WHEEL_ZOOM_HORIZONTAL_TOLERANCE = 0.35;
const AMAP_GESTURE_ZOOM_SPEED = 2.35;
const AMAP_INITIAL_ZOOM = 15.4;
const AMAP_BUTTON_ZOOM_STEP = 0.68;
const AMAP_MIN_ZOOM = 13.6;
const AMAP_MAX_ZOOM = 18.2;
const DEFAULT_FIT_VIEW_PADDING = [130, 560, 130, 110];
const DEFAULT_FIT_VIEW_MAX_ZOOM = 16.2;
// AMap's avoid order is top/bottom/left/right. The waiting card occupies the
// left side, so reserve that side and fit only the road polyline into the
// visible map area. Users can still zoom from this overview up to AMAP_MAX_ZOOM.
const WAITING_FIT_VIEW_PADDING = [110, 110, 620, 90];
const WAITING_FIT_VIEW_MAX_ZOOM = 17.1;
const FALLBACK_DRAG_SPEED = 2.8;
const FALLBACK_WHEEL_PAN_SPEED = 2.35;
const FALLBACK_PINCH_ZOOM_SPEED = 0.0072;
const FALLBACK_PINCH_MIN_STEP = 0.04;
const FALLBACK_PINCH_MAX_STEP = 0.16;
const FALLBACK_GESTURE_ZOOM_SPEED = 0.28;
const FALLBACK_MIN_ZOOM = 0.86;
const FALLBACK_MAX_ZOOM = 1.5;
const FALLBACK_BUTTON_ZOOM_STEP = 0.2;
const ZOOM_RAIL_WHEEL_STEP_RATIO = 0.14;
const FALLBACK_PAN_LIMIT_X = 420;
const FALLBACK_PAN_LIMIT_Y = 320;

type GestureLikeEvent = Event & {
  scale?: number;
};

export function TraditionalDesktopAmapLayer({
  mode = "charging",
  carwashShops = [],
  destinationLocation = AMAP_CHARGING_DESTINATION.location,
  destinationName = AMAP_CHARGING_DESTINATION.name,
  fuelStations = [],
  homeLabel = "家",
  homeLocation,
  parcelPoints = [],
  selectedCarwashShop,
  selectedFuelStation,
  selectedParcelPoint,
  routeGeometry,
  stations,
  selectedStation,
  source,
  target,
  vehicle,
  waitingRouteDataSource,
  onRefreshVehicleLocation,
  onSelectCarwashShop,
  onSelectFuelStation,
  onSelectParcelPoint,
  onSelectStation
}: {
  mode?: AmapLayerMode;
  carwashShops?: CarwashShop[];
  destinationLocation?: GeoPoint;
  destinationName?: string;
  fuelStations?: FuelStation[];
  homeLabel?: string;
  homeLocation?: GeoPoint;
  parcelPoints?: ParcelPickupPoint[];
  selectedCarwashShop?: CarwashShop;
  selectedFuelStation?: FuelStation;
  selectedParcelPoint?: ParcelPickupPoint;
  routeGeometry: RouteGeometry;
  stations: ChargingStation[];
  selectedStation: ChargingStation;
  source: ChargingStationSource | ParcelPointSource | CarwashShopSource | FuelStationSource;
  target: TraditionalDesktopDestinationTarget;
  vehicle: VehicleLocationState;
  waitingRouteDataSource?: WaitingRouteDataSource;
  onRefreshVehicleLocation: () => void;
  onSelectCarwashShop?: (shopId: string) => void;
  onSelectFuelStation?: (stationId: string) => void;
  onSelectParcelPoint?: (pointId: string) => void;
  onSelectStation: (stationId: string) => void;
}) {
  const layerRef = useRef<HTMLDivElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const overlayRefs = useRef<any[]>([]);
  const routeOverlayRefs = useRef<any[]>([]);
  const fitViewSignatureRef = useRef("");
  const [mapStatus, setMapStatus] = useState<MapStatus>("loading");
  const [mapZoom, setMapZoom] = useState(AMAP_INITIAL_ZOOM);
  const [fallbackZoom, setFallbackZoom] = useState(1);
  const [fallbackFocusRevision, setFallbackFocusRevision] = useState(0);
  const {
    vehicleLocation,
    locationStatus,
    locationSource,
    locationAccuracyM,
    locationUpdatedAt,
    locationError
  } = vehicle;
  const waitingRouteWaypoints = getDistinctWaitingWaypoints(routeGeometry.waypoints ?? []);
  const activeZoom = mapStatus === "ready" ? mapZoom : fallbackZoom;
  const activeMinZoom = mapStatus === "ready" ? AMAP_MIN_ZOOM : FALLBACK_MIN_ZOOM;
  const activeMaxZoom = mapStatus === "ready" ? AMAP_MAX_ZOOM : FALLBACK_MAX_ZOOM;
  const visiblePointCount = mode === "waiting"
    ? waitingRouteWaypoints.length
    : mode === "parcel"
    ? parcelPoints.length
    : mode === "carwash"
      ? carwashShops.length
      : mode === "refuel"
        ? fuelStations.length
      : stations.length;
  const selectedMapCenter = mode === "parcel" && selectedParcelPoint
    ? selectedParcelPoint.location
    : target === "restaurant"
      ? destinationLocation
    : mode === "carwash" && selectedCarwashShop
      ? selectedCarwashShop.location
      : mode === "refuel" && selectedFuelStation
        ? selectedFuelStation.location
    : destinationLocation;

  function setMapLayerZoom(nextZoom: number) {
    const clampedZoom = clamp(nextZoom, AMAP_MIN_ZOOM, AMAP_MAX_ZOOM);
    setMapZoom(clampedZoom);
    mapRef.current?.setZoom?.(clampedZoom);
  }

  function setFallbackLayerZoom(nextZoom: number) {
    setFallbackZoom(clamp(nextZoom, FALLBACK_MIN_ZOOM, FALLBACK_MAX_ZOOM));
  }

  function setActiveZoom(nextZoom: number) {
    if (mapStatus === "ready") {
      setMapLayerZoom(nextZoom);
      return;
    }

    setFallbackLayerZoom(nextZoom);
  }

  function stepActiveZoom(direction: 1 | -1) {
    if (mapStatus === "ready") {
      setActiveZoom(activeZoom + direction * AMAP_BUTTON_ZOOM_STEP);
      return;
    }

    setActiveZoom(activeZoom + direction * FALLBACK_BUTTON_ZOOM_STEP);
  }

  function focusActiveRoute() {
    const map = mapRef.current;
    const fitOverlays = mode === "waiting" ? routeOverlayRefs.current : overlayRefs.current;

    if (mapStatus === "ready" && map && fitOverlays.length) {
      const fitViewConfig = getFitViewConfig(mode);
      map.setFitView?.(
        fitOverlays,
        false,
        fitViewConfig.padding,
        fitViewConfig.maxZoom
      );
      syncMapZoomState(map, setMapZoom);
      return;
    }

    setFallbackZoom(1);
    setFallbackFocusRevision((current) => current + 1);
  }

  useMapPinchZoomGuard({
    layerRef,
    mapRef,
    mapStatus,
    setMapZoom,
    setFallbackZoom
  });

  useEffect(() => {
    let cancelled = false;
    let cleanupAcceleratedDrag: (() => void) | null = null;

    loadAmapScript()
      .then((AMap) => {
        if (cancelled || !mapContainerRef.current || mapRef.current) {
          return;
        }

        mapRef.current = new AMap.Map(mapContainerRef.current, {
          center: [selectedMapCenter.lng, selectedMapCenter.lat],
          zoom: AMAP_INITIAL_ZOOM,
          zooms: [AMAP_MIN_ZOOM, AMAP_MAX_ZOOM],
          viewMode: "2D",
          mapStyle: "amap://styles/darkblue",
          features: mode === "waiting"
            ? ["bg", "road", "point", "building"]
            : ["bg", "road"],
          showLabel: mode === "waiting",
          dragEnable: true,
          zoomEnable: true,
          doubleClickZoom: true,
          scrollWheel: false,
          touchZoom: true,
          touchZoomCenter: 1,
          keyboardEnable: false,
          jogEnable: false,
          pitchEnable: false,
          rotateEnable: false
        });
        mapRef.current.on?.("zoomchange", () => {
          const nextZoom = Number(mapRef.current?.getZoom?.());

          if (Number.isFinite(nextZoom)) {
            setMapZoom(clamp(nextZoom, AMAP_MIN_ZOOM, AMAP_MAX_ZOOM));
          }
        });
        cleanupAcceleratedDrag = enableAcceleratedAmapDrag(mapContainerRef.current, mapRef.current);
        setMapStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setMapStatus("fallback");
        }
      });

    return () => {
      cancelled = true;
      overlayRefs.current.forEach((overlay) => overlay?.setMap?.(null));
      overlayRefs.current = [];
      routeOverlayRefs.current = [];
      mapRef.current?.destroy?.();
      mapRef.current = null;
      fitViewSignatureRef.current = "";
      cleanupAcceleratedDrag?.();
    };
  }, [mode]);

  useEffect(() => {
    const map = mapRef.current;
    const AMap = window.AMap;

    if (!map || !AMap || mapStatus !== "ready") {
      return;
    }

    overlayRefs.current.forEach((overlay) => overlay?.setMap?.(null));
    overlayRefs.current = [];

    const routeOverlays = createRouteOverlays(AMap, routeGeometry);
    const markerOverlays = createMarkerOverlays({
      AMap,
      locationStatus,
      mode,
      onSelectParcelPoint,
      onSelectCarwashShop,
      onSelectFuelStation,
      onSelectStation,
      carwashShops,
      destinationLocation,
      destinationName,
      fuelStations,
      homeLabel,
      homeLocation,
      parcelPoints,
      routeWaypoints: waitingRouteWaypoints,
      selectedCarwashShop,
      selectedFuelStation,
      selectedParcelPoint,
      selectedStation,
      stations,
      target,
      vehicleLocation
    });
    const nextOverlays = [...routeOverlays, ...markerOverlays];
    const fitViewSignature = createFitViewSignature({
      carwashShops,
      destinationLocation,
      fuelStations,
      homeLocation,
      locationStatus,
      mode,
      parcelPoints,
      routeGeometry,
      stations,
      target,
      vehicleLocation
    });

    map.add(nextOverlays);
    overlayRefs.current = nextOverlays;
    routeOverlayRefs.current = routeOverlays;

    if (fitViewSignatureRef.current !== fitViewSignature) {
      fitViewSignatureRef.current = fitViewSignature;
      const fitViewConfig = getFitViewConfig(mode);
      const fitOverlays = mode === "waiting" ? routeOverlays : nextOverlays;
      map.setFitView(
        fitOverlays,
        false,
        fitViewConfig.padding,
        fitViewConfig.maxZoom
      );
      syncMapZoomState(map, setMapZoom);
    }
  }, [
    locationStatus,
    mapStatus,
    mode,
    destinationName,
    onSelectCarwashShop,
    onSelectFuelStation,
    onSelectParcelPoint,
    onSelectStation,
    carwashShops,
    destinationLocation,
    fuelStations,
    homeLabel,
    homeLocation,
    parcelPoints,
    routeGeometry,
    selectedCarwashShop,
    selectedFuelStation,
    selectedParcelPoint,
    selectedStation,
    stations,
    target,
    vehicleLocation
  ]);

  return (
    <div
      ref={layerRef}
      className={`traditional-desktop-amap-layer traditional-desktop-amap-layer--${mapStatus} traditional-desktop-amap-layer--${mode}`}
      aria-label={getMapAriaLabel(mode)}
    >
      <div ref={mapContainerRef} className="traditional-desktop-amap-layer__map" aria-hidden={mapStatus !== "ready"} />

      {mapStatus !== "ready" ? (
        <FallbackChargingRoute
          locationStatus={locationStatus}
          routeGeometry={routeGeometry}
          focusRevision={fallbackFocusRevision}
          zoomScale={fallbackZoom}
          vehicleLocation={vehicleLocation}
          mode={mode}
          carwashShops={carwashShops}
          destinationLocation={destinationLocation}
          destinationName={destinationName}
          fuelStations={fuelStations}
          homeLabel={homeLabel}
          homeLocation={homeLocation}
          parcelPoints={parcelPoints}
          selectedCarwashShop={selectedCarwashShop}
          selectedFuelStation={selectedFuelStation}
          selectedParcelPoint={selectedParcelPoint}
          stations={stations}
          selectedStation={selectedStation}
          target={target}
          onSelectCarwashShop={onSelectCarwashShop}
          onSelectFuelStation={onSelectFuelStation}
          onSelectParcelPoint={onSelectParcelPoint}
          onSelectStation={onSelectStation}
          onZoomScaleChange={setFallbackZoom}
        />
      ) : null}

      <div className="traditional-desktop-amap-layer__status">
        <span>{getSourceLabel(source, mapStatus, mode, waitingRouteDataSource)}</span>
        <strong>{visiblePointCount}个{getPointCountLabel(mode)}</strong>
      </div>

      <LocationStatusControl
        accuracyM={locationAccuracyM}
        detailOverride={mode === "waiting" ? destinationName : undefined}
        error={locationError}
        source={locationSource}
        status={locationStatus}
        titleOverride={mode === "waiting" ? "起点 / 终点" : undefined}
        updatedAt={locationUpdatedAt}
        onRefresh={onRefreshVehicleLocation}
      />

      <MapZoomRail
        maxZoom={activeMaxZoom}
        minZoom={activeMinZoom}
        value={activeZoom}
        onFocusRoute={focusActiveRoute}
        onZoomChange={setActiveZoom}
        onZoomIn={() => stepActiveZoom(1)}
        onZoomOut={() => stepActiveZoom(-1)}
      />
    </div>
  );
}

function useMapPinchZoomGuard({
  layerRef,
  mapRef,
  mapStatus,
  setMapZoom,
  setFallbackZoom
}: {
  layerRef: { current: HTMLDivElement | null };
  mapRef: { current: any };
  mapStatus: MapStatus;
  setMapZoom: (value: number) => void;
  setFallbackZoom: (updater: (current: number) => number) => void;
}) {
  useEffect(() => {
    let lastGestureScale = 1;

    function isInsideMapLayer(event: Event) {
      const layer = layerRef.current;
      const target = event.target;
      return Boolean(layer && target instanceof Node && layer.contains(target));
    }

    function applyAmapZoom(delta: number) {
      if (!(mapStatus === "ready" && mapRef.current?.setZoom)) {
        return false;
      }

      const currentZoom = typeof mapRef.current.getZoom === "function" ? mapRef.current.getZoom() : 15.4;
      const nextZoom = clamp(currentZoom + delta, AMAP_MIN_ZOOM, AMAP_MAX_ZOOM);
      setMapZoom(nextZoom);
      mapRef.current.setZoom(nextZoom);
      return true;
    }

    function applyZoom({
      amapDelta,
      fallbackDelta
    }: {
      amapDelta: number;
      fallbackDelta: number;
    }) {
      if (Math.abs(amapDelta) < 0.001 && Math.abs(fallbackDelta) < 0.001) {
        return;
      }

      if (applyAmapZoom(amapDelta)) {
        return;
      }

      setFallbackZoom((current) => clamp(current + fallbackDelta, FALLBACK_MIN_ZOOM, FALLBACK_MAX_ZOOM));
    }

    function handleWheel(event: WheelEvent) {
      if (!event.ctrlKey) {
        return;
      }

      stopBrowserZoom(event);

      if (!isInsideMapLayer(event)) {
        return;
      }

      const { y: wheelDeltaY } = normalizeWheelDelta(event);

      if (Math.abs(wheelDeltaY) < 0.5) {
        return;
      }

      const amapDelta = getWheelZoomDelta(wheelDeltaY, AMAP_PINCH_ZOOM_SPEED, AMAP_PINCH_MIN_STEP, AMAP_PINCH_MAX_STEP);
      const fallbackDelta = getWheelZoomDelta(wheelDeltaY, FALLBACK_PINCH_ZOOM_SPEED, FALLBACK_PINCH_MIN_STEP, FALLBACK_PINCH_MAX_STEP);

      applyZoom({ amapDelta, fallbackDelta });
    }

    function handleGestureStart(event: GestureLikeEvent) {
      stopBrowserZoom(event);
      lastGestureScale = typeof event.scale === "number" && Number.isFinite(event.scale) ? event.scale : 1;
    }

    function handleGestureChange(event: GestureLikeEvent) {
      stopBrowserZoom(event);

      if (!isInsideMapLayer(event)) {
        lastGestureScale = typeof event.scale === "number" && Number.isFinite(event.scale) ? event.scale : 1;
        return;
      }

      const currentScale = typeof event.scale === "number" && Number.isFinite(event.scale) ? event.scale : 1;
      const scaleRatio = currentScale / Math.max(lastGestureScale, 0.001);
      lastGestureScale = currentScale;

      if (!Number.isFinite(scaleRatio) || scaleRatio <= 0 || Math.abs(scaleRatio - 1) < 0.006) {
        return;
      }

      const amapDelta = clamp(Math.log2(scaleRatio) * AMAP_GESTURE_ZOOM_SPEED, -AMAP_PINCH_MAX_STEP, AMAP_PINCH_MAX_STEP);
      const fallbackDelta = clamp((scaleRatio - 1) * FALLBACK_GESTURE_ZOOM_SPEED, -FALLBACK_PINCH_MAX_STEP, FALLBACK_PINCH_MAX_STEP);

      applyZoom({ amapDelta, fallbackDelta });
    }

    function handleGestureEnd() {
      lastGestureScale = 1;
    }

    window.addEventListener("wheel", handleWheel, {
      capture: true,
      passive: false
    });
    window.addEventListener("gesturestart", handleGestureStart as EventListener, {
      capture: true,
      passive: false
    });
    window.addEventListener("gesturechange", handleGestureChange as EventListener, {
      capture: true,
      passive: false
    });
    window.addEventListener("gestureend", handleGestureEnd, {
      capture: true
    });

    return () => {
      window.removeEventListener("wheel", handleWheel, {
        capture: true
      });
      window.removeEventListener("gesturestart", handleGestureStart as EventListener, {
        capture: true
      });
      window.removeEventListener("gesturechange", handleGestureChange as EventListener, {
        capture: true
      });
      window.removeEventListener("gestureend", handleGestureEnd, {
        capture: true
      });
    };
  }, [layerRef, mapRef, mapStatus, setFallbackZoom, setMapZoom]);
}

function MapZoomRail({
  minZoom,
  maxZoom,
  value,
  onFocusRoute,
  onZoomChange,
  onZoomIn,
  onZoomOut
}: {
  minZoom: number;
  maxZoom: number;
  value: number;
  onFocusRoute: () => void;
  onZoomChange: (value: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const percent = getZoomPercent(value, minZoom, maxZoom);

  function updateFromClientY(clientY: number) {
    const rail = railRef.current;

    if (!rail) {
      return;
    }

    const rect = rail.getBoundingClientRect();
    const nextPercent = clamp((rect.bottom - clientY) / rect.height, 0, 1);
    onZoomChange(zoomFromPercent(nextPercent, minZoom, maxZoom));
  }

  return (
    <div className="traditional-desktop-amap-layer__zoom" aria-label="地图控制">
      <button
        type="button"
        className="traditional-desktop-amap-layer__zoom-focus"
        aria-label="回到路线总览"
        onClick={onFocusRoute}
      >
        <span aria-hidden="true" />
      </button>
      <button type="button" aria-label="放大地图" onClick={onZoomIn}>
        +
      </button>
      <div
        ref={railRef}
        className="traditional-desktop-amap-layer__zoom-rail"
        role="slider"
        aria-label="地图缩放级别"
        aria-orientation="vertical"
        aria-valuemin={Math.round(minZoom * 10)}
        aria-valuemax={Math.round(maxZoom * 10)}
        aria-valuenow={Math.round(value * 10)}
        tabIndex={0}
        style={{
          "--traditional-amap-zoom-percent": percent
        } as CSSProperties}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          event.currentTarget.setPointerCapture(event.pointerId);
          updateFromClientY(event.clientY);
        }}
        onPointerMove={(event) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          updateFromClientY(event.clientY);
        }}
        onWheel={(event) => {
          const { y: wheelDeltaY } = normalizeWheelDelta(event.nativeEvent);

          if (Math.abs(wheelDeltaY) < 0.5) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          const step = (maxZoom - minZoom) * ZOOM_RAIL_WHEEL_STEP_RATIO;
          onZoomChange(clamp(value + (wheelDeltaY > 0 ? -step : step), minZoom, maxZoom));
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowUp" || event.key === "ArrowRight") {
            event.preventDefault();
            onZoomIn();
          }

          if (event.key === "ArrowDown" || event.key === "ArrowLeft") {
            event.preventDefault();
            onZoomOut();
          }
        }}
      >
        <span className="traditional-desktop-amap-layer__zoom-fill" aria-hidden="true" />
        <span className="traditional-desktop-amap-layer__zoom-thumb" aria-hidden="true" />
      </div>
      <button type="button" aria-label="缩小地图" onClick={onZoomOut}>
        -
      </button>
    </div>
  );
}

function LocationStatusControl({
  accuracyM,
  detailOverride,
  error,
  source,
  status,
  titleOverride,
  updatedAt,
  onRefresh
}: {
  accuracyM?: number;
  detailOverride?: string;
  error?: string;
  source: VehicleLocationState["locationSource"];
  status: VehicleLocationStatus;
  titleOverride?: string;
  updatedAt?: number;
  onRefresh: () => void;
}) {
  const isLocating = status === "locating";

  return (
    <div className={`traditional-desktop-amap-layer__location traditional-desktop-amap-layer__location--${status}`}>
      <svg className="traditional-desktop-amap-layer__location-icon" viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r="11.5" />
        <path d="M22 2.5v39M2.5 22h39" />
        <circle cx="22" cy="22" r="3.2" />
      </svg>
      <span className="traditional-desktop-amap-layer__location-text">
        <strong>{titleOverride ?? getLocationStatusTitle(status, source)}</strong>
        <small>{detailOverride ?? getLocationStatusDetail({ accuracyM, error, source, status, updatedAt })}</small>
      </span>
      <button type="button" aria-label="刷新当前位置" disabled={isLocating} onClick={onRefresh}>
        <svg viewBox="0 0 44 44" aria-hidden="true">
          <path d="M32.5 14.3A13 13 0 1 0 34.6 25" />
          <path d="M32.5 7.7v6.6h-6.6" />
        </svg>
      </button>
    </div>
  );
}

function getLocationStatusTitle(status: VehicleLocationStatus, source: VehicleLocationState["locationSource"]) {
  if (status === "locating") {
    return "定位中";
  }

  if (status === "live") {
    return source === "amap" ? "当前高德定位" : "当前系统定位";
  }

  if (status === "failed") {
    return "定位失败";
  }

  return "演示起点";
}

function getLocationStatusDetail({
  accuracyM,
  error,
  source,
  status,
  updatedAt
}: {
  accuracyM?: number;
  error?: string;
  source: VehicleLocationState["locationSource"];
  status: VehicleLocationStatus;
  updatedAt?: number;
}) {
  if (status === "locating") {
    return "正在匹配车辆所在位置";
  }

  if (status === "live") {
    const accuracyText = accuracyM ? `精度约${Math.round(accuracyM)}m` : source === "amap" ? "高德坐标" : "系统坐标";
    const timeText = updatedAt ? ` · ${formatLocationUpdatedAt(updatedAt)}` : "";
    return `${accuracyText}${timeText}`;
  }

  if (status === "failed") {
    return getReadableLocationError(error);
  }

  return "未获取定位，展示演示起点";
}

function formatLocationUpdatedAt(updatedAt: number) {
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(new Date(updatedAt));
}

function getReadableLocationError(error: string | undefined) {
  if (error === "location-permission-denied") {
    return "定位权限未开启，点此重试";
  }

  if (error === "location-timeout") {
    return "定位超时，点此刷新";
  }

  if (error === "location-position-unavailable") {
    return "当前位置不可用，点此重试";
  }

  return "点此刷新当前位置";
}

function enableAcceleratedAmapDrag(container: HTMLElement | null, map: any) {
  if (!container || !map?.panBy) {
    return () => undefined;
  }

  let dragging = false;
  let activePointerId: number | null = null;
  let mouseDragging = false;
  let lastX = 0;
  let lastY = 0;
  let lastPointerEventAt = 0;

  function shouldIgnoreDragStart(target: EventTarget | null) {
    return target instanceof Element && Boolean(target.closest("button, .traditional-desktop-amap-marker, .traditional-desktop-amap-vehicle-marker"));
  }

  function handlePointerDown(event: PointerEvent) {
    if (event.button !== 0 || shouldIgnoreDragStart(event.target)) {
      return;
    }

    lastPointerEventAt = Date.now();
    dragging = true;
    activePointerId = event.pointerId;
    mouseDragging = false;
    lastX = event.clientX;
    lastY = event.clientY;
  }

  function handlePointerMove(event: PointerEvent) {
    lastPointerEventAt = Date.now();

    if (!dragging || event.pointerId !== activePointerId) {
      return;
    }

    const deltaX = event.clientX - lastX;
    const deltaY = event.clientY - lastY;
    lastX = event.clientX;
    lastY = event.clientY;

    if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) {
      return;
    }

    map.panBy(deltaX * AMAP_DRAG_EXTRA_SPEED, deltaY * AMAP_DRAG_EXTRA_SPEED, 0);
  }

  function handlePointerUp(event: PointerEvent) {
    lastPointerEventAt = Date.now();

    if (activePointerId !== null && event.pointerId !== activePointerId) {
      return;
    }

    dragging = false;
    activePointerId = null;
  }

  function shouldIgnoreMouseFallback() {
    return Date.now() - lastPointerEventAt < 500;
  }

  function handleMouseDown(event: MouseEvent) {
    if (shouldIgnoreMouseFallback() || event.button !== 0 || shouldIgnoreDragStart(event.target)) {
      return;
    }

    mouseDragging = true;
    lastX = event.clientX;
    lastY = event.clientY;
  }

  function handleMouseMove(event: MouseEvent) {
    if (!mouseDragging || shouldIgnoreMouseFallback()) {
      return;
    }

    const deltaX = event.clientX - lastX;
    const deltaY = event.clientY - lastY;
    lastX = event.clientX;
    lastY = event.clientY;

    if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) {
      return;
    }

    map.panBy(deltaX * AMAP_DRAG_EXTRA_SPEED, deltaY * AMAP_DRAG_EXTRA_SPEED, 0);
  }

  function handleMouseUp() {
    mouseDragging = false;
  }

  function handleWheel(event: WheelEvent) {
    const { x: wheelDeltaX, y: wheelDeltaY } = normalizeWheelDelta(event);

    if (Math.abs(wheelDeltaX) < 0.5 && Math.abs(wheelDeltaY) < 0.5) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (shouldZoomWithWheel(event, wheelDeltaX, wheelDeltaY)) {
      const currentZoom = typeof map.getZoom === "function" ? map.getZoom() : 15.4;
      const zoomDelta = getWheelZoomDelta(wheelDeltaY, AMAP_PINCH_ZOOM_SPEED, AMAP_PINCH_MIN_STEP, AMAP_PINCH_MAX_STEP);
      map.setZoom?.(clamp(currentZoom + zoomDelta, AMAP_MIN_ZOOM, AMAP_MAX_ZOOM));
      return;
    }

    const panDelta = getWheelPanDelta(event, wheelDeltaX, wheelDeltaY);
    map.panBy(-panDelta.x * AMAP_WHEEL_PAN_SPEED, -panDelta.y * AMAP_WHEEL_PAN_SPEED, 0);
  }

  container.addEventListener("pointerdown", handlePointerDown);
  container.addEventListener("mousedown", handleMouseDown);
  container.addEventListener("wheel", handleWheel, { passive: false });
  window.addEventListener("pointermove", handlePointerMove);
  window.addEventListener("pointerup", handlePointerUp);
  window.addEventListener("pointercancel", handlePointerUp);
  window.addEventListener("mousemove", handleMouseMove);
  window.addEventListener("mouseup", handleMouseUp);

  return () => {
    container.removeEventListener("pointerdown", handlePointerDown);
    container.removeEventListener("mousedown", handleMouseDown);
    container.removeEventListener("wheel", handleWheel);
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };
}

function normalizeWheelDelta(event: WheelEvent) {
  const deltaModeMultiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 120 : 1;

  return {
    x: event.deltaX * deltaModeMultiplier,
    y: event.deltaY * deltaModeMultiplier
  };
}

function getZoomPercent(value: number, minZoom: number, maxZoom: number) {
  return clamp((value - minZoom) / (maxZoom - minZoom), 0, 1);
}

function zoomFromPercent(percent: number, minZoom: number, maxZoom: number) {
  return minZoom + clamp(percent, 0, 1) * (maxZoom - minZoom);
}

function getWheelZoomDelta(deltaY: number, speed: number, minStep: number, maxStep: number) {
  const direction = deltaY > 0 ? -1 : 1;
  const step = clamp(Math.abs(deltaY) * speed, minStep, maxStep);
  return direction * step;
}

function shouldZoomWithWheel(event: WheelEvent, wheelDeltaX: number, wheelDeltaY: number) {
  if (event.ctrlKey || event.metaKey) {
    return true;
  }

  if (event.shiftKey || Math.abs(wheelDeltaY) < 0.5) {
    return false;
  }

  const isVerticalWheel = Math.abs(wheelDeltaX) <= Math.abs(wheelDeltaY) * WHEEL_ZOOM_HORIZONTAL_TOLERANCE;
  const isCoarseMouseWheel =
    event.deltaMode !== 0 ||
    Math.abs(event.deltaY) >= MOUSE_WHEEL_ZOOM_DELTA_THRESHOLD;

  return isVerticalWheel && isCoarseMouseWheel;
}

function getWheelPanDelta(event: WheelEvent, wheelDeltaX: number, wheelDeltaY: number) {
  if (event.shiftKey && Math.abs(wheelDeltaX) < 0.5) {
    return {
      x: wheelDeltaY,
      y: 0
    };
  }

  return {
    x: wheelDeltaX,
    y: wheelDeltaY
  };
}

function stopBrowserZoom(event: Event) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function createRouteOverlays(
  AMap: any,
  routeGeometry: RouteGeometry
) {
  const overlays = [
    new AMap.Polyline({
      path: routeGeometry.activePath.map(toAmapPoint),
      strokeColor: "#02101f",
      strokeOpacity: 0.92,
      strokeWeight: 19,
      lineJoin: "round",
      lineCap: "round",
      zIndex: 76
    }),
    new AMap.Polyline({
      path: routeGeometry.activePath.map(toAmapPoint),
      strokeColor: "#0a84ff",
      strokeOpacity: 0.96,
      strokeWeight: 10,
      lineJoin: "round",
      lineCap: "round",
      zIndex: 80,
      showDir: true
    }),
    new AMap.Polyline({
      path: routeGeometry.activePath.map(toAmapPoint),
      strokeColor: "#8fd6ff",
      strokeOpacity: 0.32,
      strokeWeight: 4,
      lineJoin: "round",
      lineCap: "round",
      zIndex: 82
    })
  ];

  if (routeGeometry.nextPath?.length) {
    overlays.push(
      new AMap.Polyline({
        path: routeGeometry.nextPath.map(toAmapPoint),
        strokeColor: "#5ac8fa",
        strokeOpacity: 0.42,
        strokeWeight: 7,
        strokeStyle: "dashed",
        lineDash: [14, 12],
        lineJoin: "round",
        lineCap: "round",
        zIndex: 70
      })
    );
  }

  return overlays;
}

function createMarkerOverlays({
  AMap,
  carwashShops,
  destinationLocation,
  destinationName,
  fuelStations,
  homeLabel,
  homeLocation,
  locationStatus,
  mode,
  onSelectCarwashShop,
  onSelectFuelStation,
  onSelectParcelPoint,
  onSelectStation,
  parcelPoints,
  routeWaypoints,
  selectedCarwashShop,
  selectedFuelStation,
  selectedParcelPoint,
  selectedStation,
  stations,
  target,
  vehicleLocation
}: {
  AMap: any;
  carwashShops: CarwashShop[];
  destinationLocation: GeoPoint;
  destinationName: string;
  fuelStations: FuelStation[];
  homeLabel: string;
  homeLocation?: GeoPoint;
  locationStatus: VehicleLocationStatus;
  mode: AmapLayerMode;
  onSelectCarwashShop?: (shopId: string) => void;
  onSelectFuelStation?: (stationId: string) => void;
  onSelectParcelPoint?: (pointId: string) => void;
  onSelectStation: (stationId: string) => void;
  parcelPoints: ParcelPickupPoint[];
  routeWaypoints: GeoPoint[];
  selectedCarwashShop?: CarwashShop;
  selectedFuelStation?: FuelStation;
  selectedParcelPoint?: ParcelPickupPoint;
  selectedStation: ChargingStation;
  stations: ChargingStation[];
  target: TraditionalDesktopDestinationTarget;
  vehicleLocation: GeoPoint;
}) {
  const overlays = [];

  if (mode === "waiting") {
    overlays.push(new AMap.Marker({
      position: toAmapPoint(destinationLocation),
      content: createWaitingEndpointMarkerElement(destinationName),
      anchor: "bottom-center",
      zIndex: 126
    }));

    routeWaypoints.forEach((waypoint, index) => {
      overlays.push(
        new AMap.Marker({
          position: toAmapPoint(waypoint),
          content: createWaitingWaypointMarkerElement(index),
          anchor: "center",
          zIndex: 112
        })
      );
    });

    return overlays;
  }

  overlays.push(
    new AMap.Marker({
      position: toAmapPoint(vehicleLocation),
      content: createVehicleMarkerElement(locationStatus),
      anchor: "center",
      zIndex: 126
    })
  );

  if (mode === "charging" || target === "restaurant") {
    overlays.push(
      new AMap.Marker({
        position: toAmapPoint(destinationLocation),
        content: createDestinationMarkerElement(destinationName),
        anchor: "bottom-center",
        zIndex: target === "restaurant" ? 120 : 88
      })
    );
  }

  if (mode === "parcel") {
    if (homeLocation) {
      overlays.push(
        new AMap.Marker({
          position: toAmapPoint(homeLocation),
          content: createHomeMarkerElement(homeLabel),
          anchor: "bottom-center",
          zIndex: 127
        })
      );
    }

    parcelPoints.forEach((point) => {
      const isSelected = point.id === selectedParcelPoint?.id;
      const markerContent = createParcelMarkerElement(point, isSelected);
      markerContent.addEventListener("click", (event) => {
        event.stopPropagation();
        onSelectParcelPoint?.(point.id);
      });
      overlays.push(
        new AMap.Marker({
          position: toAmapPoint(point.location),
          content: markerContent,
          anchor: "bottom-center",
          zIndex: isSelected ? 130 : 100
        })
      );
    });

    return overlays;
  }

  if (mode === "carwash") {
    carwashShops.forEach((shop) => {
      const isSelected = shop.id === selectedCarwashShop?.id;
      const markerContent = createCarwashMarkerElement(shop, isSelected);
      markerContent.addEventListener("click", (event) => {
        event.stopPropagation();
        onSelectCarwashShop?.(shop.id);
      });
      overlays.push(
        new AMap.Marker({
          position: toAmapPoint(shop.location),
          content: markerContent,
          anchor: "bottom-center",
          zIndex: isSelected ? 130 : 100
        })
      );
    });

    return overlays;
  }

  if (mode === "refuel") {
    fuelStations.forEach((station) => {
      const isSelected = station.id === selectedFuelStation?.id;
      const markerContent = createFuelMarkerElement(station, isSelected);
      markerContent.addEventListener("click", (event) => {
        event.stopPropagation();
        onSelectFuelStation?.(station.id);
      });
      overlays.push(
        new AMap.Marker({
          position: toAmapPoint(station.location),
          content: markerContent,
          anchor: "bottom-center",
          zIndex: isSelected ? 130 : 100
        })
      );
    });

    return overlays;
  }

  stations.forEach((station) => {
    const isSelected = station.id === selectedStation.id;
    const markerContent = createStationMarkerElement(station, isSelected);
    markerContent.addEventListener("click", (event) => {
      event.stopPropagation();
      onSelectStation(station.id);
    });
    overlays.push(
      new AMap.Marker({
        position: toAmapPoint(station.location),
        content: markerContent,
        anchor: "bottom-center",
        zIndex: isSelected ? 130 : 100
      })
    );
  });

  return overlays;
}

function createVehicleMarkerElement(locationStatus: VehicleLocationStatus) {
  const marker = document.createElement("div");
  const label = locationStatus === "live" ? "当前定位车辆位置" : "演示车辆位置";
  marker.className = `traditional-desktop-amap-vehicle-marker traditional-desktop-amap-vehicle-marker--${locationStatus}`;
  marker.setAttribute("aria-label", label);
  marker.title = label;
  marker.innerHTML = `
    <span class="traditional-desktop-amap-vehicle-marker__ring" aria-hidden="true"></span>
    <span class="traditional-desktop-amap-vehicle-marker__arrow" aria-hidden="true"></span>
  `;
  return marker;
}

function createDestinationMarkerElement(label: string) {
  const marker = document.createElement("div");
  marker.className = "traditional-desktop-amap-marker traditional-desktop-amap-marker--destination";
  marker.innerHTML = `
    <span class="traditional-desktop-amap-marker__pin">●</span>
    <strong>${escapeHtml(label)}</strong>
  `;
  return marker;
}

function createWaitingEndpointMarkerElement(label: string) {
  const marker = document.createElement("div");
  marker.className = "traditional-desktop-amap-waiting-endpoint";
  marker.setAttribute("aria-label", `起点和终点：${label}`);
  marker.innerHTML = `
    <span class="traditional-desktop-amap-waiting-endpoint__vehicle" aria-hidden="true"></span>
    <span>
      <small>起点 / 终点</small>
      <strong>${escapeHtml(label)}</strong>
    </span>
  `;
  return marker;
}

function createWaitingWaypointMarkerElement(index: number) {
  const marker = document.createElement("div");
  marker.className = "traditional-desktop-amap-waiting-waypoint";
  marker.setAttribute("aria-label", `绕行点${index + 1}`);
  marker.innerHTML = `<span>${index + 1}</span>`;
  return marker;
}

function createHomeMarkerElement(label: string) {
  const marker = document.createElement("div");
  marker.className = "traditional-desktop-amap-marker traditional-desktop-amap-marker--home";
  marker.innerHTML = `
    <span class="traditional-desktop-amap-marker__pin" aria-hidden="true"></span>
    <strong>${escapeHtml(label)}</strong>
  `;
  return marker;
}

function createStationMarkerElement(station: ChargingStation, selected: boolean) {
  const marker = document.createElement("button");
  marker.type = "button";
  marker.className = `traditional-desktop-amap-marker traditional-desktop-amap-marker--station${selected ? " traditional-desktop-amap-marker--selected" : " traditional-desktop-amap-marker--compact"}`;
  marker.innerHTML = `
    <span class="traditional-desktop-amap-marker__pin" aria-hidden="true"></span>
    <strong>${escapeHtml(getStationDisplayName(station))}</strong>
    <small>${escapeHtml(formatStationDistance(station.distanceM))} · ${station.availableStalls}空闲</small>
  `;
  return marker;
}

function createParcelMarkerElement(point: ParcelPickupPoint, selected: boolean) {
  const marker = document.createElement("button");
  marker.type = "button";
  marker.className = `traditional-desktop-amap-marker traditional-desktop-amap-marker--parcel${selected ? " traditional-desktop-amap-marker--selected" : " traditional-desktop-amap-marker--compact"}`;
  marker.innerHTML = `
    <span class="traditional-desktop-amap-marker__pin" aria-hidden="true"></span>
    <strong>${escapeHtml(getPickupPointDisplayName(point))}</strong>
    <small>${escapeHtml(formatPickupDistance(point.distanceM))} · ${point.pendingCount}件待取</small>
  `;
  return marker;
}

function createCarwashMarkerElement(shop: CarwashShop, selected: boolean) {
  const marker = document.createElement("button");
  marker.type = "button";
  marker.className = `traditional-desktop-amap-marker traditional-desktop-amap-marker--carwash${selected ? " traditional-desktop-amap-marker--selected" : " traditional-desktop-amap-marker--compact"}`;
  marker.innerHTML = `
    <span class="traditional-desktop-amap-marker__pin" aria-hidden="true"></span>
    <strong>${escapeHtml(getCarwashDisplayName(shop))}</strong>
    <small>${escapeHtml(formatCarwashDistance(shop.distanceM))} · ${escapeHtml(shop.priceLabel)}</small>
  `;
  return marker;
}

function createFuelMarkerElement(station: FuelStation, selected: boolean) {
  const marker = document.createElement("button");
  marker.type = "button";
  marker.className = `traditional-desktop-amap-marker traditional-desktop-amap-marker--refuel${selected ? " traditional-desktop-amap-marker--selected" : " traditional-desktop-amap-marker--compact"}`;
  marker.innerHTML = `
    <span class="traditional-desktop-amap-marker__pin" aria-hidden="true"></span>
    <strong>${escapeHtml(getFuelStationDisplayName(station))}</strong>
    <small>${escapeHtml(formatFuelDistance(station.distanceM))} · ${escapeHtml(formatFuelPrice(station.pricePerLiter))}</small>
  `;
  return marker;
}

function FallbackChargingRoute({
  carwashShops,
  destinationLocation,
  destinationName,
  fuelStations,
  focusRevision,
  homeLabel,
  homeLocation,
  locationStatus,
  mode,
  onSelectCarwashShop,
  onSelectFuelStation,
  onSelectParcelPoint,
  routeGeometry,
  zoomScale,
  vehicleLocation,
  parcelPoints,
  selectedCarwashShop,
  selectedFuelStation,
  selectedParcelPoint,
  stations,
  selectedStation,
  target,
  onSelectStation,
  onZoomScaleChange
}: {
  carwashShops: CarwashShop[];
  destinationLocation: GeoPoint;
  destinationName: string;
  fuelStations: FuelStation[];
  focusRevision: number;
  homeLabel: string;
  homeLocation?: GeoPoint;
  locationStatus: VehicleLocationStatus;
  mode: AmapLayerMode;
  onSelectCarwashShop?: (shopId: string) => void;
  onSelectFuelStation?: (stationId: string) => void;
  onSelectParcelPoint?: (pointId: string) => void;
  routeGeometry: RouteGeometry;
  zoomScale: number;
  vehicleLocation: GeoPoint;
  parcelPoints: ParcelPickupPoint[];
  selectedCarwashShop?: CarwashShop;
  selectedFuelStation?: FuelStation;
  selectedParcelPoint?: ParcelPickupPoint;
  stations: ChargingStation[];
  selectedStation: ChargingStation;
  target: TraditionalDesktopDestinationTarget;
  onSelectStation: (stationId: string) => void;
  onZoomScaleChange: (updater: (current: number) => number) => void;
}) {
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const [fallbackPan, setFallbackPan] = useState({
    x: 0,
    y: 0
  });
  const [fallbackDragging, setFallbackDragging] = useState(false);
  const fallbackRouteSignature = createRoutePathSignature(routeGeometry.activePath);

  useEffect(() => {
    dragStateRef.current = null;
    setFallbackDragging(false);
    setFallbackPan({ x: 0, y: 0 });
  }, [fallbackRouteSignature, focusRevision]);

  const waitingRouteWaypoints = getDistinctWaitingWaypoints(routeGeometry.waypoints ?? []);
  const serviceMarkerLocations = mode === "waiting"
    ? waitingRouteWaypoints
    : mode === "parcel"
    ? [
        ...(homeLocation ? [homeLocation] : []),
        ...parcelPoints.map((point) => point.location)
      ]
    : mode === "carwash"
      ? carwashShops.map((shop) => shop.location)
      : mode === "refuel"
        ? fuelStations.map((station) => station.location)
      : stations.map((station) => station.location);
  const markerLocations = [
    ...((mode === "charging" || target === "restaurant") ? [destinationLocation] : []),
    ...serviceMarkerLocations
  ];
  const fallbackBounds = createFallbackBounds(
    [
      vehicleLocation,
      ...routeGeometry.activePath,
      ...(routeGeometry.nextPath ?? []),
      ...markerLocations
    ],
    mode
  );
  const fallbackViewport = getFallbackViewport(mode);
  const fallbackWaitingPois = mode === "waiting"
    ? selectFallbackWaitingPois(fallbackBounds, destinationLocation, waitingRouteWaypoints)
    : [];
  const vehiclePoint = toFallbackPoint(vehicleLocation, fallbackBounds, fallbackViewport);
  const homePoint = homeLocation
    ? toFallbackPoint(homeLocation, fallbackBounds, fallbackViewport)
    : null;
  const destinationPoint = toFallbackPoint(destinationLocation, fallbackBounds, fallbackViewport);
  const activeRoutePath = getFallbackPathFromRoute(
    routeGeometry.activePath,
    fallbackBounds,
    fallbackViewport
  );
  const nextRoutePath = routeGeometry.nextPath
    ? getFallbackPathFromRoute(routeGeometry.nextPath, fallbackBounds, fallbackViewport)
    : "";

  return (
    <div
      className={`traditional-desktop-amap-fallback${fallbackDragging ? " traditional-desktop-amap-fallback--dragging" : ""}`}
      style={{
        "--traditional-amap-fallback-zoom": zoomScale,
        "--traditional-amap-fallback-pan-x": `${fallbackPan.x}px`,
        "--traditional-amap-fallback-pan-y": `${fallbackPan.y}px`
      } as CSSProperties}
      aria-hidden="true"
      onPointerDown={(event) => {
        if (
          event.button !== 0 ||
          (event.target instanceof Element && event.target.closest("button"))
        ) {
          return;
        }

        event.currentTarget.setPointerCapture(event.pointerId);
        setFallbackDragging(true);
        dragStateRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          panX: fallbackPan.x,
          panY: fallbackPan.y
        };
      }}
      onPointerMove={(event) => {
        const dragState = dragStateRef.current;

        if (!dragState || dragState.pointerId !== event.pointerId) {
          return;
        }

        const deltaX = (event.clientX - dragState.startX) * FALLBACK_DRAG_SPEED;
        const deltaY = (event.clientY - dragState.startY) * FALLBACK_DRAG_SPEED;

        setFallbackPan({
          x: clamp(dragState.panX + deltaX, -FALLBACK_PAN_LIMIT_X, FALLBACK_PAN_LIMIT_X),
          y: clamp(dragState.panY + deltaY, -FALLBACK_PAN_LIMIT_Y, FALLBACK_PAN_LIMIT_Y)
        });
      }}
      onPointerUp={(event) => {
        if (dragStateRef.current?.pointerId === event.pointerId) {
          dragStateRef.current = null;
          setFallbackDragging(false);
        }
      }}
      onPointerCancel={() => {
        dragStateRef.current = null;
        setFallbackDragging(false);
      }}
      onWheel={(event) => {
        const { x: wheelDeltaX, y: wheelDeltaY } = normalizeWheelDelta(event.nativeEvent);

        if (Math.abs(wheelDeltaX) < 0.5 && Math.abs(wheelDeltaY) < 0.5) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (shouldZoomWithWheel(event.nativeEvent, wheelDeltaX, wheelDeltaY)) {
          const zoomDelta = getWheelZoomDelta(
            wheelDeltaY,
            FALLBACK_PINCH_ZOOM_SPEED,
            FALLBACK_PINCH_MIN_STEP,
            FALLBACK_PINCH_MAX_STEP
          );
          onZoomScaleChange((current) => clamp(current + zoomDelta, FALLBACK_MIN_ZOOM, FALLBACK_MAX_ZOOM));
          return;
        }

        const panDelta = getWheelPanDelta(event.nativeEvent, wheelDeltaX, wheelDeltaY);
        setFallbackPan((current) => ({
          x: clamp(current.x - panDelta.x * FALLBACK_WHEEL_PAN_SPEED, -FALLBACK_PAN_LIMIT_X, FALLBACK_PAN_LIMIT_X),
          y: clamp(current.y - panDelta.y * FALLBACK_WHEEL_PAN_SPEED, -FALLBACK_PAN_LIMIT_Y, FALLBACK_PAN_LIMIT_Y)
        }));
      }}
    >
      <svg className="traditional-desktop-amap-fallback__route" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path
          className="traditional-desktop-amap-fallback__route-shadow"
          d={activeRoutePath}
        />
        <path
          className="traditional-desktop-amap-fallback__route-active"
          d={activeRoutePath}
        />
        {target === "charging" && nextRoutePath ? (
          <path
            className="traditional-desktop-amap-fallback__route-next"
            d={nextRoutePath}
          />
        ) : null}
      </svg>

      {mode !== "waiting" ? (
        <span
          className={`traditional-desktop-amap-fallback__vehicle traditional-desktop-amap-fallback__vehicle--${locationStatus}`}
          style={{
            left: `${vehiclePoint.x}%`,
            top: `${vehiclePoint.y}%`
          }}
          title={locationStatus === "live" ? "当前定位车辆位置" : "演示车辆位置"}
        >
          <span className="traditional-desktop-amap-vehicle-marker__ring" aria-hidden="true" />
          <span className="traditional-desktop-amap-vehicle-marker__arrow" aria-hidden="true" />
        </span>
      ) : null}

      {mode === "waiting" ? (
        <span
          className="traditional-desktop-amap-fallback__waiting-endpoint"
          style={{
            left: `${destinationPoint.x}%`,
            top: `${destinationPoint.y}%`
          }}
        >
          <i aria-hidden="true" />
          <span>
            <small>起点 / 终点</small>
            <strong>{destinationName}</strong>
          </span>
        </span>
      ) : mode === "charging" || target === "restaurant" ? (
        <span
          className="traditional-desktop-amap-fallback__destination"
          style={{
            left: `${destinationPoint.x}%`,
            top: `${destinationPoint.y}%`
          }}
        >
          <i />
          {destinationName}
        </span>
      ) : null}

      {mode === "parcel" && homePoint ? (
        <span
          className="traditional-desktop-amap-fallback__home"
          style={{
            left: `${homePoint.x}%`,
            top: `${homePoint.y}%`
          }}
        >
          <i />
          {homeLabel}
        </span>
      ) : null}

      {mode === "waiting" ? fallbackWaitingPois.map((poi) => {
        const point = toFallbackPoint(poi.location, fallbackBounds, fallbackViewport);
        return (
          <span
            key={poi.id}
            className="traditional-desktop-amap-fallback__poi"
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
          >
            <i aria-hidden="true" />
            <span>
              <small>{poi.category}</small>
              <strong>{poi.name}</strong>
            </span>
          </span>
        );
      }) : null}

      {mode === "waiting" ? waitingRouteWaypoints.map((waypoint, index) => {
        const point = toFallbackPoint(waypoint, fallbackBounds, fallbackViewport);
        return (
          <span
            key={`${waypoint.lng}-${waypoint.lat}-${index}`}
            className="traditional-desktop-amap-fallback__waiting-waypoint"
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
          >
            {index + 1}
          </span>
        );
      }) : null}

      {(mode === "waiting" ? [] : mode === "parcel" ? parcelPoints : mode === "carwash" ? carwashShops : mode === "refuel" ? fuelStations : stations).map((pointLike) => {
        const point = toFallbackPoint(pointLike.location, fallbackBounds, fallbackViewport);
        const selected = mode === "parcel"
          ? pointLike.id === selectedParcelPoint?.id
          : mode === "carwash"
            ? pointLike.id === selectedCarwashShop?.id
            : mode === "refuel"
              ? pointLike.id === selectedFuelStation?.id
          : pointLike.id === selectedStation.id;
        const title = mode === "parcel"
          ? getPickupPointDisplayName(pointLike as ParcelPickupPoint)
          : mode === "carwash"
            ? getCarwashDisplayName(pointLike as CarwashShop)
            : mode === "refuel"
              ? getFuelStationDisplayName(pointLike as FuelStation)
          : getStationDisplayName(pointLike as ChargingStation);
        const detail = mode === "parcel"
          ? `${formatPickupDistance((pointLike as ParcelPickupPoint).distanceM)} · ${(pointLike as ParcelPickupPoint).pendingCount}件待取`
          : mode === "carwash"
            ? `${formatCarwashDistance((pointLike as CarwashShop).distanceM)} · ${(pointLike as CarwashShop).priceLabel}`
            : mode === "refuel"
              ? `${formatFuelDistance((pointLike as FuelStation).distanceM)} · ${formatFuelPrice((pointLike as FuelStation).pricePerLiter)}`
          : `${formatStationDistance((pointLike as ChargingStation).distanceM)} · ${(pointLike as ChargingStation).availableStalls}空闲`;

        return (
          <button
            key={pointLike.id}
            type="button"
            className={[
              "traditional-desktop-amap-fallback__station",
              mode === "parcel" ? "traditional-desktop-amap-fallback__station--parcel" : "",
              mode === "carwash" ? "traditional-desktop-amap-fallback__station--carwash" : "",
              mode === "refuel" ? "traditional-desktop-amap-fallback__station--refuel" : "",
              selected ? "traditional-desktop-amap-fallback__station--selected" : "traditional-desktop-amap-fallback__station--compact"
            ].filter(Boolean).join(" ")}
            style={{
              left: `${point.x}%`,
              top: `${point.y}%`
            }}
            onClick={() => {
              if (mode === "parcel") {
                onSelectParcelPoint?.(pointLike.id);
                return;
              }

              if (mode === "carwash") {
                onSelectCarwashShop?.(pointLike.id);
                return;
              }

              if (mode === "refuel") {
                onSelectFuelStation?.(pointLike.id);
                return;
              }

              onSelectStation(pointLike.id);
            }}
          >
            <span aria-hidden="true" />
            <strong>{title}</strong>
            <small>{detail}</small>
          </button>
        );
      })}
    </div>
  );
}

function getSourceLabel(
  source: ChargingStationSource | ParcelPointSource | CarwashShopSource | FuelStationSource,
  mapStatus: MapStatus,
  mode: AmapLayerMode,
  waitingRouteDataSource?: WaitingRouteDataSource
) {
  if (mode === "waiting") {
    if (source === "loading" || mapStatus === "loading") {
      return "高德规划中";
    }

    if (waitingRouteDataSource === "amap-cache") {
      return "高德道路缓存";
    }

    if (waitingRouteDataSource === "amap-snapshot-cache") {
      return "高德道路快照缓存";
    }

    if (waitingRouteDataSource === "amap-snapshot") {
      return "高德道路快照";
    }

    if (source === "amap" && mapStatus === "ready") {
      return "高德实时路线";
    }

    return source === "amap" ? "高德驾车路线" : "龙耀路演示路线";
  }

  if (source === "loading" || mapStatus === "loading") {
    if (mode === "parcel") {
      return "高德查找驿站";
    }

    if (mode === "carwash") {
      return "高德查找洗车";
    }

    return mode === "refuel" ? "高德查找加油站" : "高德检索中";
  }

  if (source === "amap" && mapStatus === "ready") {
    if (mode === "parcel") {
      return "高德已筛选驿站";
    }

    if (mode === "carwash") {
      return "高德已筛选洗车";
    }

    return mode === "refuel" ? "高德已筛选加油站" : "高德已筛选";
  }

  if (source === "amap") {
    if (mode === "parcel") {
      return "高德驿站";
    }

    if (mode === "carwash") {
      return "高德洗车";
    }

    return mode === "refuel" ? "高德加油站" : "高德站点";
  }

  return "演示数据";
}

function getPointCountLabel(mode: AmapLayerMode) {
  if (mode === "waiting") {
    return "绕行点";
  }

  if (mode === "parcel") {
    return "取件点";
  }

  if (mode === "carwash") {
    return "洗车店";
  }

  if (mode === "refuel") {
    return "加油站";
  }

  return "快充站";
}

function getMapAriaLabel(mode: AmapLayerMode) {
  if (mode === "waiting") {
    return "高德绕路等人导航地图";
  }

  if (mode === "parcel") {
    return "高德快递取件导航地图";
  }

  if (mode === "carwash") {
    return "高德顺路洗车导航地图";
  }

  if (mode === "refuel") {
    return "高德顺路加油导航地图";
  }

  return "高德目的地补能地图";
}

function toAmapPoint(point: GeoPoint) {
  return [point.lng, point.lat];
}

function getFitViewConfig(mode: AmapLayerMode) {
  return mode === "waiting"
    ? {
        padding: WAITING_FIT_VIEW_PADDING,
        maxZoom: WAITING_FIT_VIEW_MAX_ZOOM
      }
    : {
        padding: DEFAULT_FIT_VIEW_PADDING,
        maxZoom: DEFAULT_FIT_VIEW_MAX_ZOOM
      };
}

function syncMapZoomState(map: any, setMapZoom: (value: number) => void) {
  const nextZoom = Number(map?.getZoom?.());

  if (Number.isFinite(nextZoom)) {
    setMapZoom(clamp(nextZoom, AMAP_MIN_ZOOM, AMAP_MAX_ZOOM));
  }
}

function createFitViewSignature({
  carwashShops,
  destinationLocation,
  fuelStations,
  homeLocation,
  locationStatus,
  mode,
  parcelPoints,
  routeGeometry,
  stations,
  target,
  vehicleLocation
}: {
  carwashShops: CarwashShop[];
  destinationLocation: GeoPoint;
  fuelStations: FuelStation[];
  homeLocation?: GeoPoint;
  locationStatus: VehicleLocationStatus;
  mode: AmapLayerMode;
  parcelPoints: ParcelPickupPoint[];
  routeGeometry: RouteGeometry;
  stations: ChargingStation[];
  target: TraditionalDesktopDestinationTarget;
  vehicleLocation: GeoPoint;
}) {
  const visiblePointIds = mode === "waiting"
    ? getDistinctWaitingWaypoints(routeGeometry.waypoints ?? []).map(formatPointSignature)
    : mode === "parcel"
    ? parcelPoints.map((point) => point.id)
    : mode === "carwash"
      ? carwashShops.map((shop) => shop.id)
      : mode === "refuel"
        ? fuelStations.map((station) => station.id)
        : stations.map((station) => station.id);

  return [
    mode,
    target,
    formatPointSignature(destinationLocation),
    homeLocation ? formatPointSignature(homeLocation) : "",
    locationStatus,
    formatPointSignature(vehicleLocation),
    visiblePointIds.join(","),
    routeGeometry.source,
    routeGeometry.status,
    createRoutePathSignature(routeGeometry.activePath)
  ].join("::");
}

function createRoutePathSignature(path: GeoPoint[]) {
  if (!path.length) {
    return "empty";
  }

  const lngValues = path.map((point) => point.lng);
  const latValues = path.map((point) => point.lat);
  const middlePoint = path[Math.floor(path.length / 2)];

  return [
    path.length,
    Math.min(...lngValues).toFixed(6),
    Math.max(...lngValues).toFixed(6),
    Math.min(...latValues).toFixed(6),
    Math.max(...latValues).toFixed(6),
    formatPointSignature(middlePoint)
  ].join(",");
}

function formatPointSignature(point: GeoPoint) {
  return `${point.lng.toFixed(6)},${point.lat.toFixed(6)}`;
}

function getDistinctWaitingWaypoints(waypoints: GeoPoint[]) {
  const seen = new Set<string>();

  return waypoints.filter((waypoint) => {
    const key = `${waypoint.lng.toFixed(5)},${waypoint.lat.toFixed(5)}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

interface FallbackViewport {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

const DEFAULT_FALLBACK_VIEWPORT: FallbackViewport = {
  minX: 7,
  maxX: 93,
  minY: 8,
  maxY: 90
};

const WAITING_FALLBACK_VIEWPORT: FallbackViewport = {
  minX: 54,
  maxX: 94,
  minY: 15,
  maxY: 85
};

function getFallbackViewport(mode: AmapLayerMode) {
  return mode === "waiting" ? WAITING_FALLBACK_VIEWPORT : DEFAULT_FALLBACK_VIEWPORT;
}

function toFallbackPoint(
  point: GeoPoint,
  bounds: FallbackBounds,
  viewport: FallbackViewport = DEFAULT_FALLBACK_VIEWPORT
) {
  const normalizedX = (point.lng - bounds.minLng) / (bounds.maxLng - bounds.minLng);
  const normalizedY = 1 - (point.lat - bounds.minLat) / (bounds.maxLat - bounds.minLat);

  return {
    x: clamp(
      viewport.minX + normalizedX * (viewport.maxX - viewport.minX),
      viewport.minX,
      viewport.maxX
    ),
    y: clamp(
      viewport.minY + normalizedY * (viewport.maxY - viewport.minY),
      viewport.minY,
      viewport.maxY
    )
  };
}

interface FallbackBounds {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

function selectFallbackWaitingPois(
  bounds: FallbackBounds,
  destination: GeoPoint,
  waypoints: GeoPoint[]
) {
  const selectedPois: WaitingRoutePoi[] = [];

  for (const poi of LONGYAO_WAITING_FALLBACK_POIS) {
    const isInsideBounds =
      poi.location.lng >= bounds.minLng &&
      poi.location.lng <= bounds.maxLng &&
      poi.location.lat >= bounds.minLat &&
      poi.location.lat <= bounds.maxLat;
    const overlapsRouteMarker = waypoints.some(
      (waypoint) => getGeoDistanceM(poi.location, waypoint) < 95
    );
    const overlapsPoi = selectedPois.some(
      (selectedPoi) => getGeoDistanceM(poi.location, selectedPoi.location) < 120
    );

    if (
      isInsideBounds &&
      getGeoDistanceM(poi.location, destination) >= 150 &&
      !overlapsRouteMarker &&
      !overlapsPoi
    ) {
      selectedPois.push(poi);
    }

    if (selectedPois.length >= 5) {
      break;
    }
  }

  return selectedPois;
}

function getGeoDistanceM(from: GeoPoint, to: GeoPoint) {
  const earthRadiusM = 6371000;
  const fromLat = from.lat * Math.PI / 180;
  const toLat = to.lat * Math.PI / 180;
  const deltaLat = (to.lat - from.lat) * Math.PI / 180;
  const deltaLng = (to.lng - from.lng) * Math.PI / 180;
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLng / 2) ** 2;

  return earthRadiusM * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function createFallbackBounds(
  points: GeoPoint[],
  mode: AmapLayerMode = "charging"
): FallbackBounds {
  const usablePoints = points.length ? points : [AMAP_CHARGING_DESTINATION.location];
  const lngValues = usablePoints.map((point) => point.lng);
  const latValues = usablePoints.map((point) => point.lat);
  const minLng = Math.min(...lngValues);
  const maxLng = Math.max(...lngValues);
  const minLat = Math.min(...latValues);
  const maxLat = Math.max(...latValues);
  const paddingRatio = mode === "waiting" ? 0.16 : 0.32;
  const minimumPadding = mode === "waiting" ? 0.00035 : 0.003;
  const lngPadding = Math.max((maxLng - minLng) * paddingRatio, minimumPadding);
  const latPadding = Math.max((maxLat - minLat) * paddingRatio, minimumPadding);

  return {
    minLng: minLng - lngPadding,
    maxLng: maxLng + lngPadding,
    minLat: minLat - latPadding,
    maxLat: maxLat + latPadding
  };
}

function getFallbackPath(from: { x: number; y: number }, to: { x: number; y: number }) {
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;

  return `M ${from.x} ${from.y} C ${midX - 8} ${midY + 10}, ${midX + 6} ${midY - 8}, ${to.x} ${to.y}`;
}

function getFallbackPathFromRoute(
  routePath: GeoPoint[],
  bounds: FallbackBounds,
  viewport: FallbackViewport = DEFAULT_FALLBACK_VIEWPORT
) {
  const fallbackPoints = routePath.map((point) => toFallbackPoint(point, bounds, viewport));

  if (fallbackPoints.length < 2) {
    return "";
  }

  if (fallbackPoints.length === 2) {
    return getFallbackPath(fallbackPoints[0], fallbackPoints[1]);
  }

  return fallbackPoints
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
